import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ChevronUp, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { projectsApi } from "@/lib/projects-api";
import { useScope } from "@/lib/scope";
import { workspacesApi, type Workspace } from "@/lib/workspaces-api";

// Bottom of sidebar: switch between workspaces the user is a member of.
// Switching workspaces resets the project selector to the first project
// in the new workspace.
export function WorkspaceSwitcher() {
  const { workspaceId, setWorkspaceId, setProjectId } = useScope();
  const qc = useQueryClient();

  const workspaces = useQuery({
    queryKey: ["workspaces"],
    queryFn: () => workspacesApi.list(),
  });
  const current = workspaces.data?.find((w) => w.id === workspaceId);

  async function switchTo(ws: Workspace) {
    setWorkspaceId(ws.id);
    // Reset to the first project in the target workspace; project list
    // is cached per workspace so we refetch through React Query.
    setProjectId(null);
    const targetProjects = await qc.fetchQuery({
      queryKey: ["projects", ws.id],
      queryFn: async () => {
        const previous = qc.getQueryData<Workspace[]>(["workspaces"]);
        void previous;
        return projectsApi.list();
      },
    });
    if (targetProjects[0]) setProjectId(targetProjects[0].id);
  }

  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const create = useMutation({
    mutationFn: () => workspacesApi.create({ name: newName }),
    onSuccess: async (workspace) => {
      qc.invalidateQueries({ queryKey: ["workspaces"] });
      await switchTo(workspace);
      setNewName("");
      setCreateOpen(false);
    },
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-md border bg-background px-2 py-2 text-left text-sm transition-colors hover:bg-muted"
          >
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate">
              {current?.name ?? "Select a workspace"}
            </span>
            <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" side="top" className="w-60">
          <DropdownMenuLabel className="text-xs uppercase text-muted-foreground">
            Workspaces
          </DropdownMenuLabel>
          {workspaces.data?.map((w) => (
            <DropdownMenuItem
              key={w.id}
              onSelect={() => switchTo(w)}
              className="flex items-center justify-between"
            >
              <span className="truncate">{w.name}</span>
              {w.id === workspaceId && <Check className="h-4 w-4" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator />
          <DropdownMenuItem onSelect={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New workspace</DialogTitle>
            <DialogDescription>
              Workspaces own their billing plan and member list. Each one
              gets its own set of projects.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="new-workspace-name">Name</Label>
            <Input
              id="new-workspace-name"
              autoFocus
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Acme"
              onKeyDown={(e) => {
                if (e.key === "Enter" && newName.trim()) create.mutate();
              }}
            />
            {create.error && (
              <p className="text-sm text-destructive">
                {(create.error as Error).message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!newName.trim() || create.isPending}
            >
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
